import React from 'react';
import { Container, Card, Row, Col, Table } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FaInfoCircle, FaExclamationTriangle, FaLightbulb } from 'react-icons/fa';

const NoticePeriods = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  return (
    <div style={{
      minHeight: '100vh',
      paddingTop: '2rem',
      paddingBottom: '4rem',
      width: '100%'
    }}>
      <Container>
        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-5"
        >
          <h1 className="text-white fw-bold mb-3">
            Okresy wypowiedzenia
          </h1>
          <p className="text-white-50" style={{ fontSize: '1.2rem' }}>
            Wszystko o terminach rozwiązania umowy o naukę zawodu
          </p>
        </motion.div>

        <Row className="justify-content-center">
          <Col xs={12} lg={10}>
            {/* INTRO CARD */}
            <motion.div
              {...fadeInUp}
              className="mb-4"
            >
              <Card className="border-0 shadow-lg" style={{ 
                borderRadius: '20px',
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(0, 86, 179, 0.1) 100%)'
              }}>
                <Card.Body className="p-4">
                  <div className="d-flex align-items-start mb-3">
                    <FaInfoCircle style={{ fontSize: '1.5rem', color: '#667eea', marginRight: '1rem', marginTop: '0.2rem' }} />
                    <div>
                      <h5 className="fw-bold mb-3">Kiedy stosujesz okresy wypowiedzenia?</h5>
                      <p className="mb-0" style={{ fontSize: '1rem', lineHeight: '1.6' }}>
                        Stosujesz je wtedy, kiedy pracodawca <strong>nie godzi się na rozwiązanie umowy 
                        za porozumieniem stron</strong>, tylko z zachowaniem okresu wypowiedzenia.
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </motion.div>

            {/* NOTICE PERIODS TABLE */}
            <motion.div
              {...fadeInUp}
              transition={{ delay: 0.1 }}
              className="mb-4"
            >
              <Card className="border-0 shadow-lg" style={{ 
                borderRadius: '20px',
                background: 'rgba(255,255,255,0.03)',
                border: '2px solid rgba(255,255,255,0.2)'
              }}>
                <Card.Body className="p-4">
                  <h5 className="fw-bold mb-4 text-white">
                    Tabela okresów wypowiedzenia
                  </h5>

                  <Table striped bordered hover responsive className="mb-0">
                    <thead style={{ background: 'rgba(102, 126, 234, 0.2)' }}>
                      <tr>
                        <th>Okres zatrudnienia</th>
                        <th>Okres wypowiedzenia</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>poniżej 6 miesięcy</td>
                        <td><strong>2 tygodnie</strong></td>
                      </tr>
                      <tr>
                        <td>powyżej 6 miesięcy ale poniżej 3 lat</td>
                        <td><strong>1 miesiąc</strong></td>
                      </tr>
                      <tr>
                        <td>powyżej 3 lat</td>
                        <td><strong>3 miesiące</strong></td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </motion.div>

            {/* IMPORTANT NOTE */}
            <motion.div
              {...fadeInUp}
              transition={{ delay: 0.2 }}
              className="mb-4"
            >
              <Card
                className="border-0 shadow-lg"
                style={{
                  borderRadius: '20px',
                  background: 'rgba(220, 53, 69, 0.1)',
                  border: '2px solid rgba(220, 53, 69, 0.3)'
                }}
              >
                <Card.Body className="p-4">
                  <h5 className="fw-bold mb-3">
                    <FaExclamationTriangle className="me-2" style={{ color: '#dc3545' }} />
                    Ważna informacja o 3-miesięcznym okresie
                  </h5>
                  <p className="mb-0" style={{ fontSize: '1rem', lineHeight: '1.6' }}>
                    Okres 3 miesięcy w nauce zawodu zdarza się <strong>niezwykle rzadko</strong> i dotyczy 
                    <strong> TYLKO tych osób</strong>, które nie zdały do następnej klasy i kontynuują naukę 
                    zawodu u tego samego pracodawcy.
                  </p>
                </Card.Body>
              </Card>
            </motion.div>

            {/* EXAMPLE */}
            <motion.div
              {...fadeInUp}
              transition={{ delay: 0.3 }}
              className="mb-4"
            >
              <Card
                className="border-0 shadow-lg"
                style={{
                  borderRadius: '20px',
                  background: 'rgba(255, 193, 7, 0.1)',
                  border: '2px solid rgba(255, 193, 7, 0.3)'
                }}
              >
                <Card.Body className="p-4">
                  <h5 className="fw-bold mb-3">
                    <FaLightbulb className="me-2" style={{ color: '#ffc107' }} />
                    Przykład praktyczny
                  </h5>
                  
                  <div className="mb-4 p-3 rounded" style={{
                    background: 'rgba(255, 193, 7, 0.15)',
                    borderLeft: '4px solid #ffc107'
                  }}>
                    <h6 className="fw-bold mb-3">Sytuacja:</h6>
                    <ul className="mb-0" style={{ fontSize: '1rem', lineHeight: '1.8' }}>
                      <li>Rozpocząłeś naukę: <strong>1 września 2025 r.</strong></li>
                      <li>Chcesz zmienić praktyki od: <strong>1 maja 2026 r.</strong></li>
                      <li>Pracodawca nie zgadza się na rozwiązanie za porozumieniem</li>
                    </ul>
                  </div>

                  <div className="mb-4 p-3 rounded" style={{
                    background: 'rgba(67, 233, 123, 0.1)',
                    borderLeft: '4px solid #43e97b'
                  }}>
                    <h6 className="fw-bold mb-3">Co robisz?</h6>
                    <ol className="mb-0" style={{ fontSize: '1rem', lineHeight: '1.8' }}>
                      <li className="mb-2">
                        <strong>Z końcem miesiąca marca</strong> składasz rozwiązanie umowy o naukę zawodu 
                        z zachowaniem terminu wypowiedzenia
                      </li>
                      <li className="mb-2">
                        <strong>Nadal chodzisz na praktyki do końca kwietnia</strong> (okres wypowiedzenia)
                      </li>
                      <li className="mb-2">
                        <strong>W końcu kwietnia</strong> otrzymujesz świadectwo pracy od obecnego pracodawcy 
                        (pracodawca ma 7 dni na jego wydanie, może przesłać pocztą)
                      </li>
                      <li>
                        <strong>Z tym świadectwem</strong> idziesz do nowego pracodawcy, gdzie zostaje 
                        zawarta nowa umowa o naukę zawodu
                      </li>
                    </ol>
                  </div>

                  <div className="p-3 rounded" style={{
                    background: 'rgba(102, 126, 234, 0.1)',
                    border: '2px solid rgba(102, 126, 234, 0.3)'
                  }}>
                    <p className="mb-0 fw-bold text-center" style={{ fontSize: '1.1rem' }}>
                      Efekt: Od 1 maja 2026 r. rozpoczynasz pracę u nowego pracodawcy!
                    </p>
                  </div>
                </Card.Body>
              </Card>
            </motion.div>

            {/* KEY POINTS */}
            <motion.div
              {...fadeInUp}
              transition={{ delay: 0.4 }}
            >
              <Card className="border-0 shadow-lg" style={{ 
                borderRadius: '20px',
                background: 'rgba(67, 233, 123, 0.1)',
                border: '2px solid rgba(67, 233, 123, 0.3)'
              }}>
                <Card.Body className="p-4">
                  <h5 className="fw-bold mb-4">
                    <FaInfoCircle className="me-2" style={{ color: '#43e97b' }} />
                    Kluczowe informacje
                  </h5>
                  
                  <Row>
                    <Col md={6} className="mb-3">
                      <div className="p-3 rounded h-100" style={{
                        background: 'rgba(255,255,255,0.05)'
                      }}>
                        <h6 className="fw-bold mb-2">Początek biegu wypowiedzenia</h6>
                        <p className="mb-0" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
                          Okres wypowiedzenia zawsze zaczyna biec <strong>z końcem miesiąca</strong>, 
                          w którym złożono wypowiedzenie.
                        </p>
                      </div>
                    </Col>
                    
                    <Col md={6} className="mb-3">
                      <div className="p-3 rounded h-100" style={{
                        background: 'rgba(255,255,255,0.05)'
                      }}>
                        <h6 className="fw-bold mb-2">Świadectwo pracy</h6>
                        <p className="mb-0" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
                          Pracodawca ma <strong>7 dni</strong> na wystawienie świadectwa pracy. 
                          Może je wręczyć osobiście lub wysłać pocztą.
                        </p>
                      </div>
                    </Col>
                    
                    <Col md={6} className="mb-3">
                      <div className="p-3 rounded h-100" style={{
                        background: 'rgba(255,255,255,0.05)'
                      }}>
                        <h6 className="fw-bold mb-2">Forma pisemna</h6>
                        <p className="mb-0" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
                          Wypowiedzenie umowy musi być złożone <strong>na piśmie</strong>. 
                          Przygotuj 2 egzemplarze - jeden dla pracodawcy, jeden dla siebie.
                        </p>
                      </div>
                    </Col>
                    
                    <Col md={6} className="mb-3">
                      <div className="p-3 rounded h-100" style={{
                        background: 'rgba(255,255,255,0.05)'
                      }}>
                        <h6 className="fw-bold mb-2">Podpisy</h6>
                        <p className="mb-0" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
                          Wypowiedzenie musi być podpisane przez <strong>młodocianego oraz rodzica/opiekuna</strong>, 
                          ponieważ oboje są stronami umowy.
                        </p>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </motion.div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default NoticePeriods;